import {
  ReadonlyWatchable,
  Watchable,
} from '@cotera/client/v0/actions/framework';
import type { Agent } from '@cotera/sdk/core';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { agentFromApiData } from '../../agent/agent-from-api-data';
import type { AgentNodeConfig } from '../use-agent-node-config';
import type { WatchablesFactory } from '../../watchables/watchables-factory';
import { WorkflowEntityNodeModel } from './workflow-entity-node-model';

type WorkflowAgentNodeModelProps = {
  agentId: string;
  client: Pick<TenantedClient, 'resource'>;
  watchablesFactory: Pick<
    WatchablesFactory,
    'makeAgentNodeConfigWatchable' | 'releaseAgentNodeConfigWatchable'
  >;
};

export function agentFromNodeConfig(
  agentId: string,
  config: AgentNodeConfig
): Agent | null {
  if (
    config.editorState === null ||
    config.agentKey === null ||
    config.model === null
  ) {
    return null;
  }
  return agentFromApiData({
    id: agentId,
    orgId: '',
    ownerId: '',
    agentKey: config.agentKey,
    editorState: config.editorState,
    model: config.model,
    locked: false,
    createdAt: new Date(0),
    updatedAt: new Date(0),
    title: null,
    emoji: null,
  });
}

/**
 * Per-agent view model for workflow graph agent nodes. Multiple nodes that
 * reference the same agent share one model instance (keyed by agent id).
 */
export class WorkflowAgentNodeModel extends WorkflowEntityNodeModel {
  readonly agentId: string;
  readonly configWatchable: ReadonlyWatchable<AgentNodeConfig>;
  readonly agentWatchable: ReadonlyWatchable<Agent | null>;

  private readonly _watchablesFactory: WorkflowAgentNodeModelProps['watchablesFactory'];

  constructor(props: WorkflowAgentNodeModelProps) {
    super(props.agentId);
    this.agentId = props.agentId;
    this._watchablesFactory = props.watchablesFactory;
    const eventWatchable = props.watchablesFactory.makeAgentNodeConfigWatchable(
      {
        agentId: props.agentId,
        client: props.client,
      }
    );
    this.configWatchable = eventWatchable;
    this.agentWatchable = Watchable.from((get) =>
      agentFromNodeConfig(this.agentId, get(this.configWatchable))
    );
  }

  /**
   * Intentionally no-op: {@link WorkflowFlowNode} registers this same instance
   * via ModelScopeFactory, whose cleanup would unsubscribe the shared event
   * watchable and leave the registry holding a dead watchable.
   */
  dispose(): void {
    return;
  }

  /** Called when the agent leaves the workflow graph or the editor unmounts. */
  releaseWatchable(): void {
    this._watchablesFactory.releaseAgentNodeConfigWatchable(this.agentId);
  }
}
